import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { checklistsApi } from 'src/Services/API';
import { ChecklistDto, CreateChecklistDto } from 'src/Services/API/generated';
import { trackEvent } from 'src/Services/Matomo';

type UseUpdateChecklistParams = {
  checklistId: string;
  updateData: CreateChecklistDto;
};

export const useUpdateChecklist = (
  options?: UseMutationOptions<ChecklistDto, unknown, UseUpdateChecklistParams>
) => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();

  return useMutation<ChecklistDto, unknown, UseUpdateChecklistParams>(
    ['update-checklist', assetId],
    async ({ checklistId, updateData }: UseUpdateChecklistParams) => {
      if (!assetId) {
        throw new Error('No asset selected');
      }

      const response = await checklistsApi.checklistsControllerUpdate(
        checklistId,
        assetId,
        updateData
      );

      return response.data;
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(['fetch-checklists', assetId]);
        queryClient.invalidateQueries(['fetch-checklist', assetId, variables.checklistId]);
        queryClient.invalidateQueries({ queryKey: ['fetch-checklists-submissions'] });
        options?.onSuccess?.(data, variables, context);
        trackEvent('Checklist', 'update', variables.updateData.title);
      },
    }
  );
};

export default useUpdateChecklist;
